import * as React from 'react';
// @ts-expect-error
import {components, OptionProps} from 'react-select';

import {Avatar} from 'shells/avatar/avatar';

import {stripHtml} from '../../activity-modal/tabs/calendar/helpers';

// @ts-expect-error
import {GraphQLSelectInline, Option} from './graphql-select-inline';
import {useGetParticipants} from './hooks/use-get-participants';

import styles from './graphql-select-options.module.css';

type Props = {
    selectedValues: Option[];
    onChange: (values: Option | Option[]) => any;
    onClearValue?: () => void;
    isMulti: boolean;
    clearable?: boolean;
    controlShouldRenderValue?: boolean;
    defaultOptions?: true | Option[];
    showEmail?: boolean;
    triggerChangeOnBlur?: boolean;
    disabled?: boolean;
    hasDropdownIndicator?: boolean;
    searchable?: boolean;
    openMenuOnClick?: boolean;
};

export function GraphQLSelectParticipants(props: Props) {
    const {showEmail, onChange, selectedValues, triggerChangeOnBlur = true} = props;

    const {getParticipants} = useGetParticipants();

    const handleBlur = React.useCallback(() => {
        if (triggerChangeOnBlur) {
            onChange(selectedValues);
        }
    }, [triggerChangeOnBlur, selectedValues, onChange]);

    const loadOptions = React.useCallback(
        (search?: string | null) => {
            return getParticipants(search || '').then((data) => {
                return data || [];
            });
        },
        [getParticipants]
    );

    const noOptionsMessage = React.useCallback(
        ({inputValue}) => {
            if (inputValue.length) {
                return `Nothing found matching '${inputValue}'`;
            }

            if (selectedValues.length) {
                return 'Type to search for participants…';
            }

            return null;
        },
        [selectedValues]
    );

    const customComponents = React.useMemo(() => {
        return {
            // @ts-expect-error
            Option: (optionComponentProps) => (
                <OptionComponent {...optionComponentProps} showEmail={showEmail} />
            ),
        };
    }, [showEmail]);

    return (
        <GraphQLSelectInline
            placeholder='Type to search for participants…'
            value={props.selectedValues}
            multi={props.isMulti}
            onClearValue={props.onClearValue}
            loadOptions={loadOptions}
            onChange={props.onChange}
            onBlur={handleBlur}
            noOptionsMessage={noOptionsMessage}
            components={customComponents}
            labelKey='name'
            valueKey='id'
            clearable={props.clearable}
            shouldStyleValueByType={true}
            controlShouldRenderValue={props.controlShouldRenderValue}
            defaultOptions={props.defaultOptions}
            disabled={props.disabled}
            hasDropdownIndicator={props.hasDropdownIndicator}
            searchable={props.searchable}
            openMenuOnClick={props.openMenuOnClick}
        />
    );
}

const OptionComponent = (
    props: OptionProps & {
        showEmail?: boolean;
    }
) => {
    // This is a hack to get around limitations in our old version of flow
    const spreadableProps: OptionProps = props as any;

    const userEmail = props.data.type === 'users' ? props.data.relatedName : null;
    const displayText = props.data.primaryInfo || props.data.emailAddress || userEmail;
    const cleanDisplayText = displayText ? stripHtml(displayText) : null;

    return (
        <components.Option {...spreadableProps}>
            <div className={styles['option-wrapper']}>
                <Avatar
                    avatarUrl={props.data.avatarUrl}
                    initials={props.data.initials}
                    type={props.data.type}
                />
                <div className='flex flex-dir-col'>
                    <div className={styles['option-label-with-wrap']}>{props.data.name}</div>
                    {cleanDisplayText && props.showEmail && (
                        <div className={styles['option-sublabel']}>{cleanDisplayText}</div>
                    )}
                </div>
            </div>
        </components.Option>
    );
};
